import { useEffect, useMemo } from 'react';
import { ImagePlus } from 'lucide-react';
import { Avatar } from '@aimsos/ui';

import { displayInitials } from '../utils';

const AVATAR_PREVIEW_CLASS =
  '!h-[72px] !w-[72px] !min-h-[72px] !min-w-[72px] text-lg font-semibold ring-2 ring-edge';

/**
 * Logo picker with circular preview (new file, existing URL, or placeholder / initials).
 *
 * @param {object} props
 * @param {string} props.inputId
 * @param {object} props.fileInputRef — ref to the hidden file input
 * @param {string} props.accept
 * @param {File | null} props.logoFile
 * @param {string | null | undefined} [props.existingImageUrl] — current photo when editing (ignored if `logoFile` is set)
 * @param {string} props.workspaceName — used for initials when no image
 * @param {boolean} props.dragActive
 * @param {(e: React.DragEvent) => void} props.onDragEnter
 * @param {(e: React.DragEvent) => void} props.onDragLeave
 * @param {(e: React.DragEvent) => void} props.onDrop
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onFileInputChange
 * @param {boolean} props.hasError
 * @param {string} props.hintBold
 * @param {string} props.hintFormats
 * @param {string} [props.altPreview]
 */
const WorkspaceLogoDropzone = ({
  inputId,
  fileInputRef,
  accept,
  logoFile,
  existingImageUrl,
  workspaceName,
  dragActive,
  onDragEnter,
  onDragLeave,
  onDrop,
  onFileInputChange,
  hasError,
  hintBold,
  hintFormats,
  altPreview = 'Logo preview',
}) => {
  const blobUrl = useMemo(() => {
    if (!logoFile) return null;
    return URL.createObjectURL(logoFile);
  }, [logoFile]);

  useEffect(() => {
    if (!blobUrl) return undefined;
    return () => URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const existing =
    typeof existingImageUrl === 'string' && existingImageUrl.trim().length > 0
      ? existingImageUrl.trim()
      : '';
  const previewSrc = blobUrl || (!logoFile ? existing : '');
  const hasPreview = Boolean(previewSrc);

  const initials = displayInitials(workspaceName);
  const showInitialsAvatar = !hasPreview && initials !== '?';

  return (
    <>
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={onFileInputChange}
      />
      <label
        htmlFor={inputId}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={[
          'flex min-h-[160px] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-4 py-6 transition-colors',
          dragActive
            ? 'border-brand-500 bg-brand-50/40 dark:bg-blue-950/30'
            : 'border-edge bg-surface-muted/50 hover:border-edge-strong hover:bg-surface-muted',
          hasError ? 'border-red-500 dark:border-red-500' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="flex shrink-0 justify-center">
          {hasPreview ? (
            <Avatar src={previewSrc} alt={altPreview} size="xxl" className={AVATAR_PREVIEW_CLASS} />
          ) : showInitialsAvatar ? (
            <Avatar
              initials={initials}
              alt={hintBold}
              size="xxl"
              className={`${AVATAR_PREVIEW_CLASS} border-brand-500/30 bg-brand-500/15 text-brand-800 dark:bg-brand-500/20 dark:text-brand-100`}
            />
          ) : (
            <div
              className={`inline-flex ${AVATAR_PREVIEW_CLASS} items-center justify-center rounded-full border border-edge bg-surface-elevated`}
            >
              <ImagePlus className="h-9 w-9 text-content-muted" strokeWidth={1.5} aria-hidden />
            </div>
          )}
        </div>
        <span className="text-center text-sm font-semibold text-content">{hintBold}</span>
        <span className="text-center text-xs text-content-muted">{hintFormats}</span>
        {logoFile ? (
          <span className="max-w-full truncate text-xs text-content-secondary">{logoFile.name}</span>
        ) : null}
      </label>
    </>
  );
};

export default WorkspaceLogoDropzone;
